import { ClosedEyeIcon } from "@codat/orchard-ui";
import type { TextInputProps } from "@codat/orchard-ui/dist/es/components/TextInput/TextInput";
import React, { useEffect, useState } from "react";

import { EyeIcon } from "components/icons";
import TextInput from "components/TextInput";

import styles from "./PasswordInput.module.css";

export interface PasswordInputProps extends Omit<TextInputProps, "label"> {
  inputName: string;
  setPassword: (password: string) => void;
  isPasswordValid: (isValid: boolean) => void;
  displayErrors: boolean;
  additionalValidation?: (value: string) => string;
  testId?: string;
  errorMessage?: string;
  description?: string;
  label?: string;
  cleanValue?: boolean;
}

export const InvalidPasswordErrorMessages = {
  EmptyPassword: "Please enter a password",
};

const PasswordInput: React.FC<PasswordInputProps> = ({
  inputName,
  setPassword,
  isPasswordValid,
  displayErrors,
  additionalValidation,
  testId,
  isSkeleton,
  errorMessage,
  placeholder,
  description,
  cleanValue,
  ...otherProps
}) => {
  const [value, setValue] = useState("");
  const [passwordError, setPasswordError] = useState(
    InvalidPasswordErrorMessages.EmptyPassword
  );

  useEffect(() => {
    if (cleanValue) {
      setValue("");
    }
  }, [cleanValue]);

  useEffect(() => {
    if (!value) {
      setPasswordError(InvalidPasswordErrorMessages.EmptyPassword);
    } else if (errorMessage) {
      setPasswordError(errorMessage);
    } else if (additionalValidation) {
      setPasswordError(additionalValidation(value));
    } else {
      setPasswordError("");
    }
  }, [value, errorMessage, additionalValidation, setPasswordError]);

  useEffect(() => {
    isPasswordValid(passwordError.length === 0);
  }, [passwordError, isPasswordValid]);

  const [showPassword, setShowPassword] = useState(false);

  const passwordUpdate: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    const newValue = event.target.value;
    setValue(newValue);
    setPassword(newValue);
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <TextInput
      inputName={inputName}
      name={inputName}
      data-testid={testId ?? "password-input"}
      label="Password"
      onChange={passwordUpdate}
      type={showPassword ? "text" : "password"}
      error={displayErrors && passwordError.length > 0}
      errorMessage={passwordError}
      placeholder={placeholder ?? "Type your password"}
      iconRight={
        !isSkeleton
          ? {
              icon: showPassword ? <ClosedEyeIcon /> : <EyeIcon />,
              onClick: toggleShowPassword,
              label: "Toggle view password",
              className: styles.showHideButton,
            }
          : undefined
      }
      isSkeleton={isSkeleton}
      description={description}
      autoComplete="current-password"
      value={value}
      {...otherProps}
    />
  );
};

export default PasswordInput;
